import { ChangeDetectionStrategy, Component, computed, EventEmitter, inject, input, Output } from '@angular/core';
import { ShTagListComponent } from '../../../elements/components/tag-list/tag-list.component';
import { FormValue, FormValueState } from '../../../form-controls/entities/form-value.entity';
import { TagListLabels } from '../../../form-controls/entities/tag-list-tooltips.entity';
import { Face } from '../../entities/face.entity';
import { FaceInteractionService } from '../../services/face-interaction.service';

@Component({
    standalone: true,
    selector: 'sh-face-tag',
    templateUrl: './face-tag.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ShTagListComponent]
})
export class ShFaceTagComponent {
    private readonly faceInteractionService: FaceInteractionService = inject(FaceInteractionService);

    face = input.required<Face>();
    editable = input<boolean>(false);
    disabled = input<boolean>(true);

    public labels: Partial<TagListLabels> = {
        editTooltip: 'facial-recognition.edit.tooltip',
        editWarningTitle: 'facial-recognition.edit.warning-title',
        editWarningMessage: 'facial-recognition.edit.warning-message',
        removeTooltip: 'facial-recognition.remove-person.tooltip'
    };

    $tags = computed(() => this.faceToTags(this.face()));

    @Output() personRemoved: EventEmitter<void> = new EventEmitter<void>();
    @Output() nameChange: EventEmitter<string> = new EventEmitter<string>();

    private faceToTags(face: Face): FormValue[] {
        const name = face.Person?.Name;
        if (!name) {
            return [];
        }

        return [new FormValue(name, FormValueState.SET)];
    }

    public onTagsEdit(tags: FormValue<string>[]) {
        const unsetValues = tags.filter(tag => tag.state === FormValueState.UNSET);
        const setValues = tags.filter(tag => tag.state === FormValueState.SET);

        if (unsetValues.length === 0 || setValues.length === 0) {
            return;
        }

        const oldName = unsetValues[0].value ?? undefined;
        const newName = setValues[0].value ?? undefined;

        if (oldName === newName) {
            return;
        }
        this.nameChange.emit(newName);
    }

    public onTagRemove() {
        this.personRemoved.emit();
    }

    public onTagFocus(tagId: string | null): void {
        if (!tagId) {
            this.faceInteractionService.setFaceFrameFocus(null);
            return;
        }

        this.faceInteractionService.setFaceFrameFocus(this.extractFaceId(tagId));
    }

    public onMouseenter(tagId: string): void {
        this.faceInteractionService.setFaceFrameHover(this.extractFaceId(tagId));
    }

    public onMouseleave(): void {
        this.faceInteractionService.setFaceFrameHover(null);
    }

    private extractFaceId(tagId: string): string {
        const indexOfLastSeparator: number = tagId.lastIndexOf('-');
        if (indexOfLastSeparator < 36) {
            return tagId;
        }
        const faceId = tagId.slice(0, indexOfLastSeparator);
        return faceId;
    }
}
